import { TextField, Typography } from "@mui/material";
import type { JSX } from "react";
import { useRef, useState } from "react";
import type { ValidationResult, Validator } from "@entrilia/shared/utilities/validators";
import { invalidResult, validResult } from "@entrilia/shared/utilities/validators";
import type { FieldAttribute } from "../../../api/types/objectTypes";
import { useFieldValuesContext } from "../FieldValuesContext";
import { findNextEntityInput, findPreviousEntityInput } from "../entityFieldNavigator";
import FieldValueWrapper from "./FieldValueWrapper";

interface Props {
  fieldId: string;
  value: string;
  onChange: (value: string, validationResult: ValidationResult) => void;
  validator?: Validator<string>;
  transformOnChangeFunc?: (value: string) => string;
  defaultHelperText?: string;
  displayValueComponent?: JSX.Element | JSX.Element[] | null;
  displayHoverComponent?: JSX.Element | JSX.Element[] | null;
  maxLength?: number | null;
  fieldAttributes: FieldAttribute[];
  multiline?: boolean;
  fieldInputId?: string;
  editViaDialog?: boolean;
  onEditDialogOpen?: () => void;
  readonlyReason?: string;
}

const TextValueEdit = ({
  fieldId,
  value,
  onChange,
  validator,
  transformOnChangeFunc,
  defaultHelperText,
  displayValueComponent,
  maxLength,
  fieldAttributes,
  displayHoverComponent,
  multiline,
  fieldInputId,
  editViaDialog,
  onEditDialogOpen,
  readonlyReason,
}: Props) => {
  const { validateUniqueFieldValue } = useFieldValuesContext();

  const [valueState, setValueState] = useState(value);
  const [validationState, setValidationState] = useState<ValidationResult>({
    isValid: true,
    error: defaultHelperText || "",
  });

  const [isEdit, setIsEdit] = useState(false);

  const [prevValue, setPrevValue] = useState(value);
  if (value !== prevValue) {
    setPrevValue(value);
    setValueState(value);
  }

  const inputRef = useRef<HTMLInputElement>(null);

  const validate = async (): Promise<ValidationResult> => {
    const newValue = valueState;
    if (fieldAttributes.includes("Required") && !newValue) {
      return invalidResult("Required field. Please enter the necessary information in the input field.");
    }

    const validationResult = validator ? validator(newValue) : validResult();
    if (!validationResult.isValid) {
      return validationResult;
    }

    if (fieldAttributes.includes("ObjectIdentifier")) {
      const uniqueValidationResult = await validateUniqueFieldValue(fieldId, newValue);
      if (!uniqueValidationResult.isValid) {
        return uniqueValidationResult;
      }
    }

    return validResult();
  };

  const handleValueOnLeave = async () => {
    const validationResult = await validate();
    setValidationState(validationResult);
    onChange(valueState, validationResult);
    return validationResult.isValid;
  };

  const handleValueOnChange = (newValue: string) => {
    if (transformOnChangeFunc) {
      newValue = transformOnChangeFunc(newValue);
      setValueState(newValue);
    } else {
      setValueState(newValue);
    }
  };

  const handleKeyDown = async (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      if (await handleValueOnLeave()) {
        setIsEdit(false);
      }
      return;
    }

    if (e.key === "Tab" && fieldInputId) {
      const nextElement = e.shiftKey ? findPreviousEntityInput(fieldInputId) : findNextEntityInput(fieldInputId);
      if (nextElement) {
        e.preventDefault();
        nextElement.click();
      }
      return;
    }
  };

  return (
    <FieldValueWrapper
      id={fieldInputId}
      isReadonly={fieldAttributes.includes("ReadOnly") || Boolean(readonlyReason)}
      readonlyReason={readonlyReason}
      displayValue={valueState}
      displayValueComponent={displayValueComponent}
      onLeaveEditMode={handleValueOnLeave}
      isEdit={isEdit}
      onEditChange={setIsEdit}
      displayHoverComponent={displayHoverComponent}
      editViaDialog={editViaDialog}
      onEditDialogOpen={onEditDialogOpen}
    >
      <TextField
        onFocus={(e) => e.target.setSelectionRange(e.target.value.length, e.target.value.length)}
        autoFocus
        multiline={multiline}
        maxRows={3}
        spellCheck={true}
        slotProps={{
          htmlInput: { maxLength },
          input: {
            ref: inputRef,
            endAdornment: maxLength ? (
              <Typography variant="caption" color="textSecondary">
                {maxLength - valueState.length}
              </Typography>
            ) : undefined,
          },
        }}
        error={!validationState.isValid}
        helperText={validationState.error}
        fullWidth
        value={valueState}
        onChange={(e) => handleValueOnChange(e.target.value)}
        onKeyDown={handleKeyDown}
      />
    </FieldValueWrapper>
  );
};

export default TextValueEdit;
